import React, { useCallback, useEffect, useState } from 'react';
import { useIdentity } from '@gov-nx/auth/common/hooks/useIdentity';
import { useAuthEvents } from '@gov-nx/core/events/Auth/useAuthEvents';
import { useMessageEvents } from '@gov-nx/core/events/Message/useMessageEvents';
import { LocalizeNameSpaceTypes } from '@gov-nx/core/service/translations/name-space.types';
import { useLocale } from '@gov-nx/core/service/translations/useLocale';
import { Time } from '@gov-nx/core/types/time';
import { useAuthStore } from '@gov-nx/store/auth/store';
import { useAppLogout } from '@gov-nx/store/hooks/useAppLogout';
import { useSessionStorage } from '@gov-nx/utils/web/hooks/useSessionStorage';
import { useUserInactivity } from '@gov-nx/utils/web/hooks/userInactivity';
import { useUserLogout } from '@gov-nx/web/Hooks/useUserLogout';
import { authCookiesHelper } from '../helpers/cookies';

export interface AuthProviderProps {
	children: React.ReactNode;
}

export function AuthProvider(props: AuthProviderProps) {
	const { t: tn } = useLocale(LocalizeNameSpaceTypes.Auth);
	const { toastMessageError, toastMessageWarning } = useMessageEvents();
	const { onUserLogout } = useUserLogout();
	const { logout } = useAppLogout();

	const pageAfterLoginIgnore = useSessionStorage('pageAfterLoginIgnore');

	useAuthEvents({
		onUserLogout: async (_, payload) => {
			if (payload?.isBroadcast) {
				logout();
				return;
			}

			pageAfterLoginIgnore.set('true');

			if (payload?.enforce) {
				toastMessageError(
					tn('zpravy.odhlaseni.byli-jste-automaticky-odhlaseni')
				);
			}
			await onUserLogout();
		},
		onUserInactive: () => {
			toastMessageWarning(tn('zpravy.odhlaseni.budete-automaticky-odhlaseni'));
		},
	});

	return <AuthProviderAction {...props} />;
}

export function AuthProviderAction({ children }: AuthProviderProps) {
	const { userLogout, userInactive } = useAuthEvents();
	const { fetchMe, accessToken } = useIdentity();
	const [processing, setProcessing] = useState<boolean>(true);
	const hasHydrated = useAuthStore('_hasHydrated');
	const setAccessToken = useAuthStore('setAccessToken');

	const authCookie = authCookiesHelper();
	const initAuth = useCallback(async () => {
		const accessTokenFromCookie = authCookie.getAccessToken();
		if (accessTokenFromCookie) {
			setAccessToken(accessTokenFromCookie);
			authCookie.removeAccessToken();
			authCookie.removeAuthToken();
			await fetchMe();
		} else if (accessToken) {
			await fetchMe();
		}
	}, [authCookie, accessToken, setAccessToken, fetchMe]);

	const userInactivity = useUserInactivity(
		{
			inactiveTimeout: Time['19 minutes'],
			autoLogoutTimeout: Time['20 minutes'],
			onInactive: () => userInactive(),
			onLogout: () => userLogout(),
		},
		window
	);

	useEffect(() => {
		if (!hasHydrated) return;
		setProcessing(true);
		initAuth()
			.then(() => {
				userInactivity.start();
			})
			.catch(() => userLogout())
			.finally(() => setProcessing(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [hasHydrated]);

	if (!hasHydrated || processing) {
		return null;
	}

	return children;
}
